import type { Action } from "redux";
import { combineEpics } from "redux-observable";
import type { Epic, EpicMiddleware } from "redux-observable";
import type { RootState } from "@/store/app/storeTypes";
import { coreAppEpics } from "@/store/epics/rootEpic";
import type { EpicDependencies } from "@/store/epics/epicDependencies";

type AppEpicMiddleware = EpicMiddleware<
  Action,
  Action,
  RootState,
  EpicDependencies
>;

let epicMiddlewareInstance: AppEpicMiddleware | null = null;

const injectedEpics: Epic<Action, Action, RootState, EpicDependencies>[] = [];

const rebuildAndRun = (): void => {
  if (epicMiddlewareInstance == null) {
    throw new Error(
      "Epic middleware not configured; call configureInjectableEpicMiddleware first",
    );
  }
  const merged =
    injectedEpics.length === 0
      ? coreAppEpics
      : combineEpics(coreAppEpics, ...injectedEpics);
  epicMiddlewareInstance.run(merged);
};

export const configureInjectableEpicMiddleware = (
  middleware: EpicMiddleware<unknown, unknown, RootState, EpicDependencies>,
): void => {
  epicMiddlewareInstance = middleware as AppEpicMiddleware;
  /**
   * Do not call `run(coreAppEpics)` here. redux-observable only wires `epic$ → action$`
   * after Redux invokes the middleware factory; `run()` before that emits into `epic$`
   * with no subscriber and the root epic is dropped (no epics receive actions).
   * {@link runCoreEpicsAfterStoreCreated} runs once after `configureStore`.
   */
};

/** Must run once after `configureStore` has registered `epicMiddleware` with Redux. */
export const runCoreEpicsAfterStoreCreated = (): void => {
  if (epicMiddlewareInstance == null) {
    throw new Error(
      "Epic middleware not configured; call configureInjectableEpicMiddleware first",
    );
  }
  epicMiddlewareInstance.run(coreAppEpics);
};

export const injectEpics = (
  ...epics: Epic<Action, Action, RootState, EpicDependencies>[]
): void => {
  let added = false;
  for (const epic of epics) {
    if (!injectedEpics.includes(epic)) {
      injectedEpics.push(epic);
      added = true;
    }
  }
  if (added) {
    rebuildAndRun();
  }
};
